import React, { useEffect, useState } from 'react';
import { Tree } from 'primereact/tree';
import { Button } from 'primereact/button';
import { sideMenuItems } from '../../../components/Sidebar';

const normalizeTreeNodes = (nodes) => {
    return nodes.map(node => {
        const newNode = { ...node };

        if (newNode.child) {
            newNode.children = normalizeTreeNodes(newNode.child);
            delete newNode.child;
        }

        if (newNode.children) {
            newNode.children = normalizeTreeNodes(newNode.children);
        }

        return newNode;
    });
};

const PermissionsTree = ({ initialPermissions = [], onPermissionsChange, isViewMode }) => {
    const [nodes, setNodes] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState({});
    const [expandedKeys, setExpandedKeys] = useState({});

    useEffect(() => {
        const transformedNodes = normalizeTreeNodes(sideMenuItems);
        setNodes(transformedNodes);
    }, []);

    useEffect(() => {
        const keys = {};
        const collectKeys = (nodes) => {
            for (let node of nodes) {
                keys[node.key] = true;
                if (node.children) {
                    collectKeys(node.children);
                }
            }
        };
        collectKeys(nodes);
        setExpandedKeys(keys); // Expand all nodes
    }, [nodes]);

    useEffect(() => {
        const selected = mapPermissionsToSelectedKeys(nodes, initialPermissions);
        setSelectedKeys(selected);
    }, [initialPermissions, nodes]);

    const mapPermissionsToSelectedKeys = (items, permissions) => {
        const selected = {};

        const checkNode = (node) => {
            if (!node.children?.length) {
                if (permissions.includes(node.key)) {
                    selected[node.key] = { checked: true, partialChecked: false };
                }
                return permissions.includes(node.key);
            }

            const childrenChecked = node.children.map(checkNode);
            const allChecked = childrenChecked.every(Boolean);
            const someChecked = childrenChecked.some(Boolean);

            if (allChecked) {
                selected[node.key] = { checked: true, partialChecked: false };
                return true;
            } else if (someChecked) {
                selected[node.key] = { checked: false, partialChecked: true };
            }

            return false;
        };

        items.forEach(checkNode);
        return selected;
    };

    const getCheckedKeys = (nodes, selected) => {
        let keys = [];

        for (const node of nodes) {
            if (selected[node.key]?.checked || selected[node.key]?.partialChecked) {
                keys.push(node.key);
            }
            if (node.children) {
                keys = keys.concat(getCheckedKeys(node.children, selected));
            }
        }

        return keys;
    };

    const setAccessByType = (accessType) => {
        const selected = {};

        const checkNode = (node) => {
            if (!node.children?.length) {
                if (node.key.toLowerCase().includes(accessType)) {
                    selected[node.key] = { checked: true, partialChecked: false };
                    return true;
                }
                return false;
            }

            const childStatuses = node.children.map(checkNode);
            const anyChildChecked = childStatuses.some(status => status === true);

            if (anyChildChecked) {
                const allChecked = node.children.every(child => selected[child.key]?.checked);
                selected[node.key] = {
                    checked: allChecked,
                    partialChecked: !allChecked,
                };
                return true;
            }

            return false;
        };

        nodes.forEach(checkNode);
        updateSelectedKeys(selected);
    };

    const setFullAccess = () => {
        const selected = {};

        const selectAll = (node) => {
            selected[node.key] = { checked: true, partialChecked: false };
            if (node.children) {
                node.children.forEach(selectAll);
            }
        };

        nodes.forEach(selectAll);
        updateSelectedKeys(selected);
    };

    const resetAllAccess = () => {
        updateSelectedKeys({});
    };

    const updateSelectedKeys = (updatedSelectedKeys) => {
        setSelectedKeys(updatedSelectedKeys);
        const permissionKeys = getCheckedKeys(nodes, updatedSelectedKeys);
        onPermissionsChange(permissionKeys);
    };

    return (
        <div>
            <div className='flex gap-10 align-items-center action-btn-wrapper mb-2'>
                <label className='required mr-auto'>Permissions</label>
                <Button label="Read Access" size="small" icon="pi pi-eye" onClick={() => setAccessByType('r')} className="p-button-secondary" />
                <Button label="Write Access" size="small" icon="pi pi-pencil" onClick={() => setAccessByType('w')} className="p-button-secondary" />
                <Button label="Delete Access" size="small" icon="pi pi-trash" onClick={() => setAccessByType('d')} className="p-button-secondary" />
                <Button label="Full Access" size="small" icon="pi pi-check-circle" onClick={setFullAccess} className="p-button-secondary" />
                <Button label="Reset All Access" size="small" icon="pi pi-times-circle" onClick={resetAllAccess} className="p-button-secondary" />
            </div>

            <Tree
                className="w-full"
                value={nodes}
                selectionMode="checkbox"
                selectionKeys={selectedKeys}
                onSelectionChange={(e) => updateSelectedKeys(e.value)}
                expandedKeys={expandedKeys}
                onToggle={(e) => setExpandedKeys(e.value)} // Handle toggle to update expanded keys
                readOnly={isViewMode}
            />
        </div>
    );
};

export default PermissionsTree;
